import { StreamMap, useApiClient } from '@m43/core';
import { MessagePayload } from '@m43/sockets-mark43';
import { DispositionsTabResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';
import { TAB_QUERY_CACHE_TIME } from '../tabs/constants';

export function useIntegratedEventDispositionsQuery(props: { eventId?: number }) {
    const apiClient = useApiClient();
    const { eventId } = props;
    const dispositionsTabViewQuery = useQuery(
        ['GetDispositionsTabView', eventId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<DispositionsTabResource.GetDispositionsTabView>({
                    method: 'GET',
                    url: `dispositions_tab/${queryKey[1]}/`,
                })
                .then((data) => {
                    return data.data.data;
                });
        },
        {
            cacheTime: TAB_QUERY_CACHE_TIME,
            enabled: false,
        }
    );

    const onMessage: (message: MessagePayload<StreamMap, 'nudge'>) => number[] = useCallback(
        (message) => {
            // Listen for event clearing notifications
            // Note that there is the `EC` key which is specifically
            // for 'event clearing', but this does not fire
            // when an event is reactivated
            // (on the other hand, `E` is fired for both clearings / reactivations")
            const eventNudgeKeys = ['E'];

            for (const key of eventNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (eventId && entry.id === eventId) {
                        return [eventId];
                    }
                }
            }

            return [];
        },
        [eventId]
    );

    useIntegratedStreams({
        query: dispositionsTabViewQuery,
        streamNames: ['nudge'],
        promiseBufferCallback: () => dispositionsTabViewQuery.refetch(),
        onMessage,
    });

    return dispositionsTabViewQuery;
}
